/**
 * MPCI 申报凭证
 *
 * 设计来源：设计文档 §7.2.1（MPCI）/ §7.3（区块：申报信息 / 单号 / 运输 / 参与方 / 集装箱 / 货物）
 */
import type { CertificateBuilder, CertificateContext, CertificateData, CertificateSection } from '../types'
import { registerCertificateBuilder, CERTIFICATE_TITLES } from './registry'
import {
  DASH,
  FieldCollector,
  findParty,
  dedupeMissing,
  dictContainerType,
  dictPackingUnit,
  dictPort,
  filingStatus,
  formatDateTime,
  gridSection,
  latestReceipt,
  logsSection,
  partyAddress,
  pick,
  receiptValue,
  rowsOf,
  tableSection,
  text,
  yesNo,
  type CertRow,
} from './shared'

/** 拼箱：优先 consolidation，缺失时按 blCount > 1 推定 */
function consolidationText(detail: CertRow): string {
  if (typeof detail.consolidation === 'boolean') return yesNo(detail.consolidation)
  const rawCount = text(detail.blCount)
  if (!rawCount) return ''
  const count = Number(rawCount)
  return Number.isFinite(count) ? (count > 1 ? 'Yes' : 'No') : ''
}

export const buildMpciCertificate: CertificateBuilder = (
  context: CertificateContext
): CertificateData => {
  const detail = context.detail ?? {}
  const dict = context.dict
  const receipt = latestReceipt(context.receipts)
  const missing: string[] = []

  const info = new FieldCollector(missing)
  info
    .add('Entry Type', pick(detail, 'entryType'))
    .add(
      'Filing Status',
      filingStatus(
        pick(receipt, 'receiptStatus') || pick(detail, 'receiptStatus'),
        context.statusLabel,
        pick(receipt, 'sendType')
      )
    )
    .add('Notification Message', pick(receipt, 'receiptMessage') || pick(detail, 'receiptMessage'))
    .add('Registration Date', formatDateTime(pick(receipt, 'receiptTime') || pick(detail, 'receiptTime')))

  const numbers = new FieldCollector(missing)
  numbers
    .add('Declarant MPCI', pick(detail, 'declarantCode'))
    .add('HBL', pick(detail, 'hblNo'))
    .add('Parent BL MPCI', pick(detail, 'previousDeclarantCode') || pick(detail, 'declarantCode'))
    .add('Parent BL#', pick(detail, 'mblNo') || pick(detail, 'previousBlNo'))
    .add('On Behalf', yesNo(detail.onBehalf))
    .add('Filer MPCI', pick(detail, 'filerCode'))
    .add('Consolidation', consolidationText(detail))
    .optional('Sub Declarant Code', pick(detail, 'subDeclarantCode'))

  const transport = new FieldCollector(missing)
  transport
    .add('Vessel', pick(detail, 'vesselName'))
    .add('Voyage', pick(detail, 'voyage'))
    .add('ETD', formatDateTime(pick(detail, 'etd')))
    .add('ETA', formatDateTime(pick(detail, 'eta')))
    .add('Place Of Receipt', dictPort(dict, detail.placeOfReceiptId))
    .add('Loading', dictPort(dict, detail.loadingPortId))
    .add('Discharge', dictPort(dict, detail.dischargePortId))
    .add('Place Of Delivery', dictPort(dict, detail.placeOfDeliveryId))

  const parties = new FieldCollector(missing)
  parties
    .add('Shipper', partyAddress(findParty(detail, 'SHIPPER'), { dict }))
    .add('Consignee', partyAddress(findParty(detail, 'CONSIGNEE'), { dict }))
    .add('Forwarding Agent', partyAddress(findParty(detail, 'LOADING_AGENT'), { dict }))
    .optional('Discharge Agent', partyAddress(findParty(detail, 'DISCHARGE_AGENT'), { dict }))

  const containerRows = rowsOf(detail, 'containerList').map(row => [
    text(row.containerNo) || DASH,
    dictContainerType(dict, row.containerType) || DASH,
    text(row.sealNo) || DASH,
    text(row.sealNo2) || DASH,
  ])

  const cargoRows = rowsOf(detail, 'cargoList').map(row => [
    text(row.cargoName) || DASH,
    text(row.hsCode) || DASH,
    text(row.quantity) || DASH,
    dictPackingUnit(dict, row.unitTypeId) || DASH,
    text(row.grossWeight) || DASH,
  ])

  const sections: CertificateSection[] = [
    gridSection(2, info.fields, 'DECLARATION'),
    gridSection(4, numbers.fields, 'REFERENCE'),
    gridSection(4, transport.fields, 'TRANSPORT'),
    gridSection(2, parties.fields, 'PARTY INFORMATION'),
    tableSection(
      'CONTAINERS',
      ['Container No.', 'Type', 'SealNo1', 'SealNo2'],
      containerRows,
      missing
    ),
    tableSection(
      'CARGOS',
      ['Goods Description', 'HS Code', 'Qty', 'Unit', 'Weight'],
      cargoRows,
      missing
    ),
    logsSection(context.receipts, missing),
  ]

  return {
    product: 'MPCI',
    title: CERTIFICATE_TITLES.MPCI,
    issuedAt: formatDateTime(
      receiptValue(context.receipts, 'receiptTime') ||
        pick(detail, 'receiptTime') ||
        pick(detail, 'dateModified')
    ),
    sections,
    missing: dedupeMissing(missing),
  }
}

registerCertificateBuilder('MPCI', buildMpciCertificate)
